import { Stack } from '@mui/material'
import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import { useAccount } from '@app/src/context/AccountContext'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { capitalize } from 'lodash'
import React from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import type { User } from '@app/src/types/resourceExplorer'
import { NotificationSeverity } from '@app/src/wf-constants'
import { useDrawer } from '../DrawerContext'
import { OwnerRestrictionField } from '../../Form/User/OwnerRestrictionField'
import { useForm } from 'react-hook-form'
import { RolePicker } from './Picker/RolePicker'
import { normalizeOwnerRestriction } from '@app/src/utils/rolePermissions'

export type DrawerViewEditUserProps = {
  user: User
} & Omit<DrawerViewProps, 'title'>

type UpdateUserBody = {
  organizationId?: number
  solutionType?: string
  isOwnerRestricted?: boolean
} & Pick<User, 'email' | 'role'>

const DrawerViewEditUser: React.FC<DrawerViewEditUserProps> = ({ user, ...props }) => {
  const { closeDrawer } = useDrawer()
  const { account } = useAccount()
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { solution } = useAuthentication().scope
  const { mutateAsync: mutateRole, isPending: isMutatingRole } = useUpdateResource<UpdateUserBody>()
  const { handleSubmit, control, watch } = useForm<UpdateUserBody>({
    defaultValues: {
      role: user?.role.toLowerCase(),
      isOwnerRestricted: user?.isOwnerRestricted || false,
    },
  })

  const selectedRole = watch('role')
  const isOwnerRestricted = watch('isOwnerRestricted')

  const onSubmit = async (values: UpdateUserBody): Promise<void> => {
    const colleague: UpdateUserBody = {
      email: user.email,
      role: capitalize(values.role),
      organizationId: account?.organization?.id,
      solutionType: capitalize(solution),
      isOwnerRestricted: normalizeOwnerRestriction(values.role, solution, values.isOwnerRestricted ?? false),
    }

    await mutateRole(
      { url: endpoints.saveSolutionMembership, body: colleague },
      {
        onSuccess: () => {
          //The colleagues doesn't update directly so adding a timeout solves this.
          setTimeout(() => {
            showSnackbar({
              message: formatMessage({ id: 'notifications.successfulUpdateRole' }),
              severity: NotificationSeverity.success,
            })

            queryClient.invalidateQueries({ queryKey: [FetchKey.Colleague] })
            closeDrawer()
          }, 1500)
        },
        onError: error => {
          showErrorNotification({ requestError: error, disableAutoClose: true })
        },
      },
    )
  }

  const hasNewSettings = (): boolean => {
    return user.role.toLowerCase() !== selectedRole || isOwnerRestricted !== (user.isOwnerRestricted || false)
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'permissions.editPermission' })}
      subTitle={user.name}
      buttons={[
        {
          label: formatMessage({ id: 'general.cancel' }),
          variant: 'outlined',
          onClick: closeDrawer,
          loading: isMutatingRole,
        },
        {
          label: formatMessage({ id: 'general.update' }),
          variant: 'contained',
          onClick: handleSubmit(onSubmit),
          loading: isMutatingRole,
          disabled: !hasNewSettings(),
        },
      ]}
      {...props}
      onFormSubmit={handleSubmit(onSubmit)}
    >
      <Stack sx={{ px: 2, justifyContent: 'space-between', flexGrow: 1 }}>
        <RolePicker control={control} name={'role' satisfies keyof UpdateUserBody} disabled={isMutatingRole} />

        <OwnerRestrictionField
          control={control}
          name={'isOwnerRestricted' satisfies keyof UpdateUserBody}
          selectedRole={selectedRole}
          solution={solution}
        />
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewEditUser
